// src/components/admin/AdminRoleManager.js
import React, { useState, useEffect } from "react";
import { Shield, User, Check, Plus, Edit, Trash } from "lucide-react";

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddRole, setShowAddRole] = useState(false);
  const [activeTab, setActiveTab] = useState("roles");

  // Fetch roles, permissions, and admin users
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        
        // Fetch roles
        const rolesResponse = await fetch('/api/admin/roles', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!rolesResponse.ok) {
          throw new Error('Failed to fetch roles');
        }
        
        const rolesData = await rolesResponse.json();
        setRoles(rolesData);
        
        // Fetch permissions
        const permissionsResponse = await fetch('/api/admin/roles/permissions/all', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!permissionsResponse.ok) {
          throw new Error('Failed to fetch permissions');
        }
        
        const permissionsData = await permissionsResponse.json();
        setPermissions(permissionsData);
        
        // Fetch admin users
        const usersResponse = await fetch('/api/admin/clients?status=admin', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!usersResponse.ok) {
          throw new Error('Failed to fetch admin users');
        }
        
        const usersData = await usersResponse.json();
        setUsers(usersData.filter(user => user.isAdmin));
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Add this function in the RoleManagement component
const addMissingPermissions = async () => {
  try {
    setLoading(true);
    const token = localStorage.getItem("token");
    
    // Define new modules that are missing
    const newModules = ['invoices', 'leads', 'teams'];
    const actions = ['create', 'read', 'update', 'delete', 'manage', 'publish'];
    
    // Add missing permissions
    const response = await fetch('/api/admin/roles/permissions/add-missing', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ modules: newModules, actions })
    });
    
    if (!response.ok) {
      throw new Error('Failed to add missing permissions');
    }
    
    // Refresh permissions
    const permissionsResponse = await fetch('/api/admin/roles/permissions/all', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (permissionsResponse.ok) {
      const permissionsData = await permissionsResponse.json();
      setPermissions(permissionsData);
    }
    
    // Show success message
    setError(null);
    alert('Missing permissions added successfully!');
    
  } catch (err) {
    setError(err.message);
    console.error('Error adding missing permissions:', err);
  } finally {
    setLoading(false);
  }
};



  // Group permissions by module
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.module]) {
      acc[permission.module] = [];
    }
    acc[permission.module].push(permission);
    return acc;
  }, {});

  // Handle role selection
  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setNewRoleName(role.name);
    setNewRoleDescription(role.description || "");
    setSelectedPermissions(role.permissions.map(p => p._id));
  };

  // Handle permission selection
  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  // Save role changes
  const handleSaveRole = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const roleData = {
        name: newRoleName,
        description: newRoleDescription,
        permissions: selectedPermissions
      };
      
      const method = selectedRole ? 'PUT' : 'POST';
      const endpoint = selectedRole 
        ? `/api/admin/roles/${selectedRole._id}`
        : '/api/admin/roles';
      
      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(roleData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to save role');
      }
      
      const updatedRole = await response.json();
      
      if (selectedRole) {
        // Updating existing role
        setRoles(roles.map(r => r._id === updatedRole._id ? updatedRole : r));
      } else {
        // Adding new role
        setRoles([...roles, updatedRole]);
      }
      
      // Reset form state
      setShowAddRole(false);
      setSelectedRole(null);
      setNewRoleName("");
      setNewRoleDescription("");
      setSelectedPermissions([]);
      
    } catch (err) {
      setError(err.message);
      console.error('Error saving role:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete a role
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) {
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`/api/admin/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete role');
      }
      
      // Remove role from list
      setRoles(roles.filter(r => r._id !== roleId));
      
      // Reset selection if needed
      if (selectedRole && selectedRole._id === roleId) {
        setSelectedRole(null);
        setNewRoleName("");
        setNewRoleDescription("");
        setSelectedPermissions([]);
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Error deleting role:', err);
    } finally {
      setLoading(false);
    }
  };

  // Assign role to user
  const handleAssignRole = async (userId, roleId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      const response = await fetch(`/api/admin/roles/assign/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ roleId })
      });
      
      if (!response.ok) {
        throw new Error('Failed to assign role');
      }
      
      // Update user in the users list
      setUsers(users.map(user => {
        if (user._id === userId) {
          return { ...user, role: roleId };
        }
        return user;
      }));
      
    } catch (err) {
      setError(err.message);
      console.error('Error assigning role:', err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle module permissions
  const handleToggleModule = (module) => {
    const modulePermissionIds = groupedPermissions[module].map(p => p._id);
    const allSelected = modulePermissionIds.every(id => selectedPermissions.includes(id));
    
    if (allSelected) {
      // Remove all permissions from this module
      setSelectedPermissions(prev => 
        prev.filter(id => !modulePermissionIds.includes(id))
      );
    } else {
      // Add all permissions from this module
      setSelectedPermissions(prev => {
        const currentSelected = new Set(prev);
        modulePermissionIds.forEach(id => currentSelected.add(id));
        return Array.from(currentSelected);
      });
    }
  };

  // Initialize system with default roles and permissions
  const handleInitSystem = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // First create permissions
      await fetch('/api/admin/roles/permissions/init', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Then create default roles
      await fetch('/api/admin/roles/presets/init', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Refresh data
      const rolesResponse = await fetch('/api/admin/roles', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const permissionsResponse = await fetch('/api/admin/roles/permissions/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (rolesResponse.ok && permissionsResponse.ok) {
        setRoles(await rolesResponse.json());
        setPermissions(await permissionsResponse.json());
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Error initializing system:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">

    {/* Header */}
      <h2 className="text-2xl font-semibold">Role Management</h2>
      <div className="container mx-auto p-1 space-y-6 bg-gray-50 order border-gray-200 rounded-2xl">

      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}
      
      <div className="mb-6">
        <div className="flex border-b">
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'roles' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
            onClick={() => setActiveTab('roles')}
          >
            Roles & Permissions
          </button>
          <button
            className={`px-4 py-2 font-medium ${activeTab === 'users' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
            onClick={() => setActiveTab('users')}
          >
            User Assignments
          </button>
        </div>
      </div>
      
      {activeTab === 'roles' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Roles List */}
          <div className="col-span-1 bg-white p-4 rounded shadow">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium">Roles</h2>
              <button
                onClick={() => {
                  setSelectedRole(null);
                  setNewRoleName("");
                  setNewRoleDescription("");
                  setSelectedPermissions([]);
                  setShowAddRole(true);
                }}
                className="flex items-center text-blue-500 hover:text-blue-700"
              >
                <Plus size={16} className="mr-1" />
                <span>Add Role</span>
              </button>
            </div>
            
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {roles.map(role => (
                <div
                  key={role._id}
                  className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${selectedRole?._id === role._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
                  onClick={() => handleRoleSelect(role)}
                >
                  <div className="flex justify-between">
                    <h3 className="font-medium">{role.name}</h3>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {role.permissions.length} permissions
                    </span>
                  </div>
                  {role.description && (
                    <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                  )}
                </div>
              ))}
              
              {roles.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  <p>No roles defined yet</p>
                  <button
                    onClick={handleInitSystem}
                    className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Initialize System
                  </button>
                </div>
              )}
            </div>                          
            <button
              onClick={addMissingPermissions}
              className="mt-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            >
              Add Missing Permissions
            </button>
          </div>
          
          {/* Role Details / Permissions */}
          <div className="col-span-2 bg-white p-4 rounded shadow">
            {(selectedRole || showAddRole) ? (
              <div>
                <h2 className="text-lg font-medium mb-4">
                  {selectedRole ? `Edit Role: ${selectedRole.name}` : 'Add New Role'}
                </h2>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Name
                  </label>
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={(e) => setNewRoleName(e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Content Editor"
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newRoleDescription}
                    onChange={(e) => setNewRoleDescription(e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe the role's purpose"
                  />
                </div>
                
                <div className="mb-4">
                  <h3 className="text-md font-medium mb-2">Permissions</h3>
                  
                  <div className="max-h-[400px] overflow-y-auto border rounded p-4">
                    {Object.keys(groupedPermissions).map(module => {
                      const modulePermissions = groupedPermissions[module];
                      const allSelected = modulePermissions.every(p => 
                        selectedPermissions.includes(p._id)
                      );
                      
                      return (
                        <div key={module} className="mb-4">
                          <div 
                            className="flex items-center cursor-pointer mb-2"
                            onClick={() => handleToggleModule(module)}
                          >
                            <div className={`h-4 w-4 border rounded mr-2 flex items-center justify-center ${allSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                              {allSelected && <Check size={12} className="text-white" />}
                            </div>
                            <h4 className="font-medium uppercase">{module}</h4>
                          </div>
                          
                          <div className="ml-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {modulePermissions.map(permission => (
                              <div 
                                key={permission._id} 
                                className="flex items-center cursor-pointer"
                                onClick={() => handlePermissionToggle(permission._id)}
                              >
                                <div className={`h-4 w-4 border rounded mr-2 flex items-center justify-center ${selectedPermissions.includes(permission._id) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'}`}>
                                  {selectedPermissions.includes(permission._id) && <Check size={12} className="text-white" />}
                                </div>
                                <span className="text-sm">{permission.action}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="flex justify-between">
                  {selectedRole && (
                    <button
                      onClick={() => handleDeleteRole(selectedRole._id)}
                      className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                      disabled={loading}
                    >
                      Delete Role
                    </button>
                  )}
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setSelectedRole(null);
                        setShowAddRole(false);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    
                    <button
                      onClick={handleSaveRole}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      disabled={loading || !newRoleName}
                    >
                      {loading ? 'Saving...' : 'Save Role'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Shield size={48} className="mb-4 opacity-30" />
                <p>Select a role to edit its permissions</p>
                <p className="text-sm">or add a new role to get started</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white p-4 rounded shadow">
          <h2 className="text-lg font-medium mb-4">Role Assignments</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Change Role
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map(user => (
                  <tr key={user._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-200 rounded-full">
                          <User size={20} className="text-gray-500" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {roles.find(r => r._id === user.role)?.name || 'No Role'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={user.role || ''}
                        onChange={(e) => handleAssignRole(user._id, e.target.value)}
                        className="block w-full max-w-xs px-3 py-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      >
                        <option value="">Select Role</option>
                        {roles.map(role => (
                          <option key={role._id} value={role._id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
                
                {users.length === 0 && (
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                      No admin users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default RoleManagement;